import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Alert, CircularProgress,
  IconButton, Tooltip, Link, Dialog, DialogTitle, DialogContent, Switch, FormControlLabel, Grid, Divider,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, LineChart, Line,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

// Reading view of the FedTrade desk (KnightRider:/root/FedTrade). Contract:
// docs/remote-prompts/FEDTRADE_DASHBOARD.md. Live days come from the scanner's
// database (backend), the 2026-11-02 sample from its Docs/dashboard.md. No order button, no broker call,
// no position size or dollar profit — the paper book stays closed until the
// operator stores a size in the desk JSON.

interface Checkpoint {
  name: string; label: string; scheduled_for: string; status: string; price: number | null; note?: string;
}
interface PaperLine {
  eligible: boolean; status: string; blockers: string[]; entry_checkpoint: string; exit_checkpoint: string;
  entry_price: number | null; exit_price: number | null; direction_return_pct: number | null;
  size: number | null; checkpoints: Checkpoint[];
  // 9:35 stock/option fills, written by the scanner (detail_json.entities[].what_if).
  // The only source for the what-if cells; absent = not_stored.
  what_if?: WhatIf | null;
}
interface MarketCheck {
  source?: string; status?: string; flag?: string; price?: string | number | null; previous_close?: string | number | null;
  move_pct?: number | null; volume?: number | null; optionable?: boolean | null;
}
interface WhatIf {
  stock?: {
    side?: string; entry_price?: number | null; exit_price?: number | null;
    result_per_share?: number | null; status?: string;
  };
  option?: {
    right?: string; contract?: string; expiration?: string; strike?: number | null;
    entry_bid?: number | null; entry_ask?: number | null; entry_mid?: number | null;
    exit_bid?: number | null; exit_ask?: number | null; exit_mid?: number | null;
    result_per_contract?: number | null; status?: string;
  };
}
interface Entity {
  company_name: string; direction: string; model_ticker: string; yahoo_symbol: string; listing: string;
  listing_note: string; exposure: string; rationale: string; quote: string; page: string;
  ticker_source?: string; paper_line: PaperLine; market_check?: MarketCheck | null;
}
interface Filing {
  document_number: string; title: string; type: string; filing_type: string; agency: string; pdf_url: string;
  source: string; material: boolean; score: number; confidence: number | null; catalyst: string;
  unchanged_from_prior: boolean; cash_deposits_unchanged: boolean; effective: string; what_changed: string;
  cleared: boolean; suggestion: string; entities: Entity[]; event_stage: string; lane: string; repeat_of?: string;
  session_kind?: string; first_seen_at?: string; score_anchor?: string;
}
interface Summary {
  scanned_at: string; model: string; market_check: string; orders: string; alert_min_score: number;
  desk: number; filtered: number; cleared: number; failed: number; quote_source: string; session_date: string;
  session_kind: string; tradable_that_session: boolean; near_misses: number; paper_book: string;
  paper_size: number | null; observation_days?: number; observation_days_required?: number;
  dropped?: number | null; pending?: number | null; source?: string;
}
interface Session { summary: Summary; filings: Filing[]; }
interface History {
  paper_book: { status: string; size: number | null; observation_days: number; observation_days_required: number };
  sessions: Session[];
}
interface HistoryResp {
  history: History; source: string; doc_mtime: string; fetched_at: string; live_window?: boolean; stale: boolean; error?: string;
}
interface QuotePoint { ts: string; price: number; change_pct: number | null; volume: number | null; }
interface LiveQuote extends QuotePoint {
  symbol: string; currency: string | null; exchange: string | null; sparkline: { t: string; c: number }[];
}
interface QuoteEntry { quote: LiveQuote | null; first: QuotePoint | null; latest: QuotePoint | null; }

const MIN_SCORE = 7;
const SCORE_BANDS = 'Bands: 1–2 procedure · 3–4 initiation or a sunset that only continues an order · 5–6 new preliminary cash deposit or proposed directive · 7+ only a final change.';
const FOOTER = 'A reading view of a public filing. Not an order. The paper book is closed until a size is set. A human decides whether a later ticket is real.';
const LANE_COLOR: Record<string, 'success' | 'warning' | 'default'> = { cleared: 'success', near_miss: 'warning', context: 'default' };
const STAGE_LABEL: Record<string, string> = {
  proposed: 'PROPOSED — not a final rule', final: 'FINAL', notice: 'NOTICE', guidance: 'GUIDANCE',
  preliminary: 'PRELIMINARY — not final', sunset_continuation: 'SUNSET — order continued', application: 'APPLICATION',
};
const KIND_LABEL: Record<string, string> = {
  after_close_manual: 'after-close manual scan', burst: 'morning burst', intraday: 'intraday',
  'burst+intraday': 'morning burst + intraday',
};

const etTime = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) + ' ET';
};
const fmtPrice = (p: number | null | undefined) => (p == null ? '' : Number(p).toLocaleString('en-US', { maximumFractionDigits: 4 }));
const fmtPct = (p: number | null | undefined) => (p == null ? '' : `${p > 0 ? '+' : ''}${Number(p).toFixed(2)}%`);
const fmtVol = (v: number | null | undefined) => (v == null ? '—' : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : String(v));

// Main list = a name we can trade: the scanner's Alpaca market check is ok with a price
const hasPrice = (v: unknown) => v !== '' && v != null;
const tradeable = (e: Entity) => e.market_check?.status === 'ok' && hasPrice(e.market_check.price);
const isTradeableFiling = (f: Filing) => (f.entities || []).some(tradeable);
const untradeableReason = (e: Entity): string => {
  const t = (e.model_ticker || '').trim().toUpperCase();
  const mc = e.market_check;
  if (!t || ['N/A', 'NA', 'NONE', 'PRIVATE', 'UNLISTED', 'UNKNOWN'].includes(t)) return 'no ticker';
  if (mc?.status === 'unsupported' || t.includes('.') || t.includes(':')) return 'not a US listing';
  if (mc) return 'quote failed';
  return 'no market check';
};
// A SHORT the market rose against: show direction, score and move only — no explanation
const shortRose = (e: Entity) => e.direction === 'SHORT' && (e.market_check?.move_pct ?? 0) > 0;

const checkpoint = (pl: PaperLine | undefined, name: string) => pl?.checkpoints?.find((c) => c.name === name);

// Underlying percent from the 9:30 checkpoint to the next business-day 9:30,
// signed in the idea's direction. Blank until both prices exist.
const directionReturn = (e: Entity): number | null => {
  const entry = e.paper_line?.entry_price;
  const exit = e.paper_line?.exit_price;
  if (entry == null || exit == null || !entry) return null;
  const r = e.direction === 'SHORT' ? (entry - exit) / entry : (exit - entry) / entry;
  return r * 100;
};

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const recommendation = (f: Filing): string | null => {
  if (f.score >= MIN_SCORE && !f.material) return 'Score conflict. No trade suggestion.';
  if (f.lane === 'cleared') return f.suggestion ? f.suggestion : null;
  if (f.lane === 'near_miss') return 'Near miss. Kept for the score history. No paper ticket.';
  return 'Below 7. No trade suggestion.';
};

const Footer: React.FC = () => (
  <Typography variant="caption" sx={{ display: 'block', mt: 3, pt: 1, borderTop: '1px solid #333', color: 'text.secondary' }}>
    {FOOTER}
  </Typography>
);

const MiniBar: React.FC<{ title: string; data: { name: string; n: number }[]; vertical?: boolean; refAt?: string; empty?: string }> = ({ title, data, vertical, refAt, empty }) => (
  <Card sx={{ bgcolor: '#2d2d2d', height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>{title}</Typography>
      {data.length === 0 || data.every((d) => !d.n) ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{empty || 'None.'}</Typography>
      ) : (
        <ResponsiveContainer width="100%" height={vertical ? Math.max(120, data.length * 26) : 160}>
          {vertical ? (
            <BarChart data={data} layout="vertical" margin={{ left: 10, right: 10 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fill: '#aaa', fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={170} tick={{ fill: '#ccc', fontSize: 11 }} />
              <RTooltip contentStyle={{ background: '#222', border: '1px solid #444' }} />
              <Bar dataKey="n" fill="#90caf9" />
            </BarChart>
          ) : (
            <BarChart data={data}>
              <XAxis dataKey="name" tick={{ fill: '#aaa', fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#aaa', fontSize: 11 }} width={28} />
              <RTooltip contentStyle={{ background: '#222', border: '1px solid #444' }} />
              {refAt && <ReferenceLine x={refAt} stroke="#ffb74d" strokeDasharray="4 3" label={{ value: 'brief gate 7', fill: '#ffb74d', position: 'top', fontSize: 11 }} />}
              <Bar dataKey="n" fill="#90caf9" />
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

const QuoteBox: React.FC<{ symbol: string; q?: QuoteEntry; loading: boolean }> = ({ symbol, q, loading }) => {
  const live = q?.quote;
  return (
    <Box sx={{ border: '1px solid #444', borderRadius: 1, p: 1, minWidth: 210 }}>
      <Typography variant="caption" sx={{ color: '#ffb74d', fontWeight: 600 }}>Display quote · {symbol}</Typography>
      {live ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{fmtPrice(live.price)} {live.currency || ''}</Typography>
            <Typography variant="body2" color="text.secondary">{fmtPct(live.change_pct)}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">vol {fmtVol(live.volume)}{live.exchange ? ` · ${live.exchange}` : ''}</Typography>
          {live.sparkline?.length > 1 && (
            <ResponsiveContainer width="100%" height={36}>
              <LineChart data={live.sparkline}>
                <YAxis hide domain={['dataMin', 'dataMax']} />
                <Line type="monotone" dataKey="c" stroke="#90caf9" dot={false} strokeWidth={1.5} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
          {q?.first && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              first seen {fmtPrice(q.first.price)} @ {etTime(q.first.ts)}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">{loading ? 'loading…' : 'quote unavailable'}</Typography>
      )}
    </Box>
  );
};

// Scanner's Alpaca IEX check at scoring time. Not the what-if fill, not the display quote.
const MarketCheckLine: React.FC<{ mc: MarketCheck }> = ({ mc }) => {
  const ok = mc.status === 'ok';
  const price = ok && mc.price !== '' && mc.price != null ? Number(mc.price) : null;
  const prev = ok && mc.previous_close !== '' && mc.previous_close != null ? Number(mc.previous_close) : null;
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary">Market check ({mc.source || 'scanner'} IEX, at scoring):</Typography>
      {mc.flag && <Chip size="small" variant="outlined" label={mc.flag} color={ok ? 'warning' : 'default'} sx={{ height: 20, fontSize: 11 }} />}
      {price != null ? (
        <Typography variant="caption">
          {fmtPrice(price)}{prev != null ? ` vs prev close ${fmtPrice(prev)}` : ''}{mc.move_pct != null ? ` (${fmtPct(mc.move_pct)})` : ''}
          {mc.volume != null ? ` · vol ${fmtVol(mc.volume)}` : ''}{mc.optionable != null ? ` · ${mc.optionable ? 'optionable' : 'no options'}` : ''}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">{mc.status === 'ok' ? 'no price' : mc.status}</Typography>
      )}
    </Box>
  );
};

const CheckpointTable: React.FC<{ pl: PaperLine }> = ({ pl }) => (
  <Table size="small" sx={{ '& td, & th': { py: 0.25, fontSize: 12, borderColor: '#3a3a3a' } }}>
    <TableHead>
      <TableRow><TableCell>Checkpoint</TableCell><TableCell>Status</TableCell><TableCell>Price</TableCell><TableCell>Note</TableCell></TableRow>
    </TableHead>
    <TableBody>
      {(pl.checkpoints || []).map((c) => (
        <TableRow key={c.name}>
          <TableCell>{c.label}</TableCell>
          <TableCell>{c.status}</TableCell>
          <TableCell>{c.status === 'filled' ? fmtPrice(c.price) : ''}</TableCell>
          <TableCell sx={{ color: 'text.secondary' }}>{c.note || ''}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const EntityRow: React.FC<{ e: Entity; marketCheck: string; quotes: Record<string, QuoteEntry>; quotesLoading: boolean }> = ({ e, marketCheck, quotes, quotesLoading }) => {
  const chartable = e.listing === 'listed' && !!e.yahoo_symbol;
  return (
    <Box sx={{ border: '1px solid #3a3a3a', borderRadius: 1, p: 1.5, mt: 1 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <Box sx={{ flex: '1 1 380px' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={e.direction} color={e.direction === 'LONG' ? 'success' : e.direction === 'SHORT' ? 'error' : 'default'} variant="outlined" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{e.company_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              model ticker: {e.model_ticker || '—'}{e.ticker_source ? ` (${e.ticker_source})` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Yahoo: {chartable ? e.yahoo_symbol : `none (${e.listing})`}
            </Typography>
            {!e.market_check && marketCheck === 'off' && <Chip size="small" label="priced-in: market check off" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: e.listing === 'listed' ? 'text.secondary' : '#ffb74d' }}>
            {e.listing_note}
          </Typography>
          {!shortRose(e) && e.rationale && <Typography variant="body2" sx={{ mt: 0.5 }}>{e.rationale}</Typography>}
          {!shortRose(e) && e.exposure && <Typography variant="body2" color="text.secondary"><b>Exposure:</b> {e.exposure}</Typography>}
          {e.market_check && <MarketCheckLine mc={e.market_check} />}
          {!shortRose(e) && e.quote && (
            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: '#ccc' }}>
              “{e.quote}”{e.page ? ` — p. ${e.page}` : ''}
            </Typography>
          )}
        </Box>
        {chartable && <QuoteBox symbol={e.yahoo_symbol} q={quotes[e.yahoo_symbol.toUpperCase()]} loading={quotesLoading} />}
      </Box>
      {e.paper_line && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Paper line: <b>{e.paper_line.status}</b>{e.paper_line.eligible ? ' · eligible' : ''}
            {e.paper_line.blockers?.length ? ` · blockers: ${e.paper_line.blockers.join(' · ')}` : ''}
          </Typography>
          <CheckpointTable pl={e.paper_line} />
        </Box>
      )}
    </Box>
  );
};

const FilingCard: React.FC<{ f: Filing; marketCheck: string; quotes: Record<string, QuoteEntry>; quotesLoading: boolean }> = ({ f, marketCheck, quotes, quotesLoading }) => {
  const rec = recommendation(f);
  const shown = (f.entities || []).filter(tradeable);
  const silent = shown.some(shortRose);
  return (
    <Card sx={{ bgcolor: '#2d2d2d', mb: 2, opacity: f.repeat_of ? 0.6 : 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Chip size="small" label={f.lane.replace('_', ' ')} color={LANE_COLOR[f.lane] || 'default'} />
          {f.event_stage ? (
            <Chip size="small" variant="outlined" label={STAGE_LABEL[f.event_stage] || f.event_stage.toUpperCase()} color={f.event_stage === 'proposed' ? 'warning' : 'default'} />
          ) : (
            <Chip size="small" variant="outlined" label="stage not recorded" sx={{ color: 'text.secondary' }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, ml: 1 }}>{f.score}<Typography component="span" variant="caption" color="text.secondary">/10 score</Typography></Typography>
          <Typography variant="h6" sx={{ fontWeight: 400 }}>{f.confidence ?? '—'}<Typography component="span" variant="caption" color="text.secondary">/10 confidence</Typography></Typography>
          <Chip size="small" variant="outlined" label={f.material ? 'material' : 'not material'} />
          {f.unchanged_from_prior && <Chip size="small" color="info" label="unchanged from prior" />}
          {f.cash_deposits_unchanged && <Chip size="small" color="info" label="cash deposits unchanged" />}
          {f.repeat_of && <Chip size="small" label={`repeat of ${f.repeat_of} — no second ticket`} />}
          {f.session_kind && (
            <Chip size="small" variant="outlined" label={`${KIND_LABEL[f.session_kind] || f.session_kind}${f.first_seen_at ? ` · seen ${etTime(f.first_seen_at)}` : ''}`} />
          )}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{f.title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {f.agency} · {f.type}{f.filing_type ? ` (${f.filing_type})` : ''} · {f.catalyst} · {f.document_number} ·{' '}
          {f.pdf_url ? <Link href={f.pdf_url} target="_blank" rel="noopener noreferrer">PDF</Link> : 'no PDF'}
        </Typography>
        {!silent && f.score_anchor && <Typography variant="body2" sx={{ mt: 1 }}><b>Score anchor:</b> {f.score_anchor}</Typography>}
        {f.what_changed && <Typography variant="body2" sx={{ mt: 1 }}><b>What changed:</b> {f.what_changed}</Typography>}
        <Typography variant="body2" color="text.secondary"><b>Effective:</b> {f.effective || '—'}</Typography>
        {rec && (
          <Alert severity={f.lane === 'cleared' && f.material ? 'info' : 'warning'} icon={false} sx={{ mt: 1, py: 0 }}>
            {rec}
          </Alert>
        )}
        {shown.map((e, i) => (
          <EntityRow key={`${f.document_number}-${i}`} e={e} marketCheck={marketCheck} quotes={quotes} quotesLoading={quotesLoading} />
        ))}
        {shown.length < (f.entities || []).length && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            +{(f.entities || []).length - shown.length} not tradeable (in the drawer below).
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

interface TapeRow { date: string; f: Filing; e: Entity; }

const FedTradeView: React.FC = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [resp, setResp] = useState<HistoryResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(0);
  const [sessionDate, setSessionDate] = useState<string>('');
  const [quotes, setQuotes] = useState<Record<string, QuoteEntry>>({});
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [nameHistory, setNameHistory] = useState<string | null>(null);
  const latestRef = useRef<string>('');
  const [flt, setFlt] = useState({ date: 'all', lane: 'all', listedOnly: false, direction: 'all', agency: 'all', catalyst: 'all', paper: 'all' });

  const fetchHistory = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const r = await fetch(`/api/fedtrade/history${refresh ? '?refresh=true' : ''}`, { headers });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      const data: HistoryResp = await r.json();
      setResp(data);
      setError(null);
      const dates = data.history.sessions.map((s) => s.summary.session_date);
      const latest = dates[dates.length - 1] || '';
      setSessionDate((cur) => {
        // Follow the newest day unless an older session was picked on purpose
        if (!cur || !dates.includes(cur) || cur === latestRef.current) return latest;
        return cur;
      });
      latestRef.current = latest;
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchQuotes = useCallback(async (date: string) => {
    if (!date) return;
    setQuotesLoading(true);
    try {
      const r = await fetch(`/api/fedtrade/quotes?session=${encodeURIComponent(date)}`, { headers });
      if (r.ok) setQuotes((await r.json()).quotes || {});
    } catch {
      // cards show "quote unavailable"
    } finally {
      setQuotesLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchHistory();
    const id = setInterval(() => fetchHistory(), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchHistory]);
  useEffect(() => {
    setQuotes({});
    fetchQuotes(sessionDate);
    const id = setInterval(() => fetchQuotes(sessionDate), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [sessionDate, fetchQuotes]);

  const history = resp?.history;
  const sessions = useMemo(() => history?.sessions || [], [history]);
  const session = sessions.find((s) => s.summary.session_date === sessionDate);

  // One idea per document_number: later repeats stay on their session card only
  const ideas = useMemo(() => sessions.flatMap((s) => s.filings.filter((f) => !f.repeat_of).map((f) => ({ date: s.summary.session_date, f }))), [sessions]);
  const tape: TapeRow[] = useMemo(() => ideas.flatMap(({ date, f }) => (f.entities || []).map((e) => ({ date, f, e }))), [ideas]);

  const counts = useMemo(() => ({
    cleared: ideas.filter(({ f }) => f.lane === 'cleared').length,
    near: ideas.filter(({ f }) => f.lane === 'near_miss').length,
    context: ideas.filter(({ f }) => f.lane !== 'cleared' && f.lane !== 'near_miss').length,
  }), [ideas]);

  if (loading && !resp) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>FedTrade</Typography>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Session</InputLabel>
        <Select label="Session" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)}>
          {[...sessions].reverse().map((s) => (
            <MenuItem key={s.summary.session_date} value={s.summary.session_date}>{s.summary.session_date}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip title={resp ? `${resp.source} · file updated ${resp.doc_mtime} · fetched ${etTime(resp.fetched_at)}` : ''}>
        <IconButton onClick={() => { fetchHistory(true); fetchQuotes(sessionDate); }}><RefreshIcon /></IconButton>
      </Tooltip>
      {session && (
        <Chip size="small" variant="outlined" color={session.summary.source === 'scanner_db' ? 'success' : 'default'}
          label={session.summary.source === 'scanner_db' ? 'live scanner database' : 'sample (Docs/dashboard.md)'} />
      )}
      {resp && <Typography variant="caption" color="text.secondary">updated {etTime(resp.fetched_at)}{resp.live_window ? ' · refreshing every 5 min' : ''}</Typography>}
      {resp?.stale && <Chip size="small" color="warning" label="desk unreachable — showing last copy" />}
    </Box>
  );

  if (error && !resp) {
    return <Box>{header}<Alert severity="error">FedTrade desk unavailable: {error}</Alert><Footer /></Box>;
  }

  return (
    <Box>
      {header}
      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 2, borderBottom: '1px solid #333' }}>
        <Tab label="Session" /><Tab label="Tape" /><Tab label="Paper book" /><Tab label="Calibration" />
      </Tabs>
      {view === 0 && session && <SessionView s={session} quotes={quotes} quotesLoading={quotesLoading} />}
      {view === 1 && <TapeView rows={tape} flt={flt} setFlt={setFlt} onSymbol={setNameHistory} />}
      {view === 2 && <PaperBookView rows={tape} history={history!} counts={counts} />}
      {view === 3 && <CalibrationView ideas={ideas} rows={tape} counts={counts} required={history?.paper_book?.observation_days_required || 20} />}
      <Footer />
      <Dialog open={!!nameHistory} onClose={() => setNameHistory(null)} maxWidth="md" fullWidth>
        <DialogTitle>{nameHistory} — every session that named it</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Session</TableCell><TableCell>Document</TableCell><TableCell>Score</TableCell><TableCell>Lane</TableCell>
              <TableCell>Stage</TableCell><TableCell>Direction</TableCell><TableCell>Company</TableCell><TableCell>Title</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {tape.filter((r) => r.e.yahoo_symbol && r.e.yahoo_symbol.toUpperCase() === nameHistory).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.date}</TableCell><TableCell>{r.f.document_number}</TableCell><TableCell>{r.f.score}</TableCell>
                  <TableCell>{r.f.lane}</TableCell><TableCell>{r.f.event_stage}</TableCell><TableCell>{r.e.direction}</TableCell>
                  <TableCell>{r.e.company_name}</TableCell><TableCell>{r.f.title}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const SessionView: React.FC<{ s: Session; quotes: Record<string, QuoteEntry>; quotesLoading: boolean }> = ({ s, quotes, quotesLoading }) => {
  const sum = s.summary;
  const filings = [...s.filings].sort((a, b) => b.score - a.score || (b.confidence || 0) - (a.confidence || 0));
  const cards = filings.filter(isTradeableFiling);
  const notTradeable = filings.length - cards.length;
  const drawer = filings.flatMap((f) => (f.entities || []).filter((e) => !tradeable(e)).map((e) => ({ f, e })));
  const nearMisses = filings.filter((f) => f.lane === 'near_miss' && !f.repeat_of);
  const countBy = (key: (f: Filing) => string) => {
    const m: Record<string, number> = {};
    filings.forEach((f) => { const k = key(f) || '—'; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([name, n]) => ({ name, n })).sort((a, b) => b.n - a.n);
  };
  const scoreHist = Array.from({ length: 10 }, (_, i) => ({ name: String(i + 1), n: filings.filter((f) => f.score === i + 1).length }));
  const traded = filings.flatMap((f) => f.entities || []).filter(tradeable);
  const longShort = ['LONG', 'SHORT'].map((d) => ({ name: d, n: traded.filter((e) => e.direction === d).length }));
  const funnel = [
    { label: 'Desk', n: sum.desk }, { label: 'Filtered', n: sum.filtered },
    { label: 'Not tradeable', n: notTradeable }, { label: `Cleared ${sum.alert_min_score ?? MIN_SCORE}`, n: sum.cleared },
    { label: 'Near misses', n: sum.near_misses },
  ];
  return (
    <Box>
      <Card sx={{ bgcolor: '#2d2d2d', mb: 2 }}>
        <CardContent>
          <Typography variant="h6">
            {sum.session_date} · scanned {etTime(sum.scanned_at)} · model {sum.model}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
            <Chip size="small" label={`orders: ${sum.orders}`} />
            <Chip size="small" label={`market check: ${sum.market_check}`} />
            <Chip size="small" label={`paper book: ${(sum.paper_book || 'not_open').replace('_', ' ')}`} />
            <Chip size="small" label="display quotes: Yahoo" />
            <Chip size="small" label={`minimum score ${sum.alert_min_score ?? MIN_SCORE}`} />
            <Chip size="small" variant="outlined" label={`session: ${KIND_LABEL[sum.session_kind] || sum.session_kind}`} />
            <Chip size="small" variant="outlined" color={sum.tradable_that_session ? 'success' : 'default'} label={sum.tradable_that_session ? 'tradable that session' : 'not tradable that session'} />
          </Box>
          {sum.session_kind === 'after_close_manual' && (
            <Typography variant="body2" color="text.secondary">
              Scored at {etTime(sum.scanned_at)}, after the close — not tradable that morning.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', mt: 2, flexWrap: 'wrap' }}>
            {funnel.map((st, i) => (
              <React.Fragment key={st.label}>
                {i > 0 && <Typography sx={{ alignSelf: 'center', color: 'text.secondary' }}>›</Typography>}
                <Box sx={{ border: '1px solid #444', borderRadius: 1, px: 2, py: 1, minWidth: 110 }}>
                  <Typography variant="caption" color="text.secondary">{st.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{st.n ?? '—'}</Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Dropped by the filter: {sum.dropped ?? (sum.desk != null && sum.filtered != null ? sum.desk - sum.filtered : '—')}
            {sum.failed ? ` · failed: ${sum.failed}` : ''}{sum.pending ? ` · still processing: ${sum.pending}` : ''}
          </Typography>
          {!sum.cleared && <Typography variant="body2" sx={{ mt: 1 }}>None cleared 7.</Typography>}
          {nearMisses.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Near misses: {nearMisses.map((f) => `${f.title} (${f.score})`).join(' · ')}
            </Typography>
          )}
        </CardContent>
      </Card>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6} lg={3}>
          <MiniBar title="Score (line = brief gate, not a measured edge)" data={scoreHist} refAt="7" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{SCORE_BANDS}</Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={3}><MiniBar title="Agency" data={countBy((f) => f.agency)} vertical /></Grid>
        <Grid item xs={12} md={6} lg={3}><MiniBar title="Catalyst" data={countBy((f) => f.catalyst)} vertical /></Grid>
        <Grid item xs={12} md={6} lg={3}><MiniBar title="Long vs short (tradeable names)" data={longShort} empty="No tradeable names." /></Grid>
      </Grid>
      {cards.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No tradeable name this session.</Typography>
      )}
      {cards.map((f) => (
        <FilingCard key={f.document_number} f={f} marketCheck={sum.market_check} quotes={quotes} quotesLoading={quotesLoading} />
      ))}
      {drawer.length > 0 && (
        <Accordion key={sum.session_date} defaultExpanded={false} disableGutters sx={{ bgcolor: '#262626', mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" color="text.secondary">Not tradeable ({drawer.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small" sx={{ '& td, & th': { fontSize: 12, py: 0.25, borderColor: '#3a3a3a' } }}>
              <TableHead><TableRow>
                <TableCell>Score</TableCell><TableCell>Document</TableCell><TableCell>Title</TableCell><TableCell>Company</TableCell><TableCell>Reason</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {drawer.map(({ f, e }, i) => (
                  <TableRow key={i}>
                    <TableCell>{f.score}</TableCell><TableCell>{f.document_number}</TableCell>
                    <TableCell>{f.title}</TableCell><TableCell>{e.company_name}</TableCell>
                    <TableCell>{untradeableReason(e)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

type Flt = { date: string; lane: string; listedOnly: boolean; direction: string; agency: string; catalyst: string; paper: string };

const TapeView: React.FC<{ rows: TapeRow[]; flt: Flt; setFlt: (f: Flt) => void; onSymbol: (s: string) => void }> = ({ rows, flt, setFlt, onSymbol }) => {
  const opts = (key: (r: TapeRow) => string) => Array.from(new Set(rows.map(key).filter(Boolean))).sort();
  const filters: { k: keyof Flt; label: string; values: string[] }[] = [
    { k: 'date', label: 'Date', values: opts((r) => r.date) },
    { k: 'lane', label: 'Lane', values: opts((r) => r.f.lane) },
    { k: 'direction', label: 'Direction', values: opts((r) => r.e.direction) },
    { k: 'agency', label: 'Agency', values: opts((r) => r.f.agency) },
    { k: 'catalyst', label: 'Catalyst', values: opts((r) => r.f.catalyst) },
    { k: 'paper', label: 'Paper status', values: opts((r) => r.e.paper_line?.status || '') },
  ];
  const shown = rows
    .filter((r) => (flt.date === 'all' || r.date === flt.date)
      && (flt.lane === 'all' || r.f.lane === flt.lane)
      && (!flt.listedOnly || r.e.listing === 'listed')
      && (flt.direction === 'all' || r.e.direction === flt.direction)
      && (flt.agency === 'all' || r.f.agency === flt.agency)
      && (flt.catalyst === 'all' || r.f.catalyst === flt.catalyst)
      && (flt.paper === 'all' || r.e.paper_line?.status === flt.paper))
    .sort((a, b) => a.date.localeCompare(b.date) || b.f.score - a.f.score);
  const cp = (r: TapeRow, n: string) => {
    const c = checkpoint(r.e.paper_line, n);
    return c?.status === 'filled' ? fmtPrice(c.price) : '';
  };
  const cols = ['Session', 'Document', 'Lane', 'Score', 'Conf', 'Material', 'Stage', 'Catalyst', 'Agency', 'Dir', 'Company',
    'Model ticker', 'Yahoo', 'Listing', 'Paper', 'Blockers', '8:46', '9:30', '10:30', 'Next open', 'Dir. return', 'Size'];
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        {filters.map(({ k, label, values }) => (
          <FormControl key={k} size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{label}</InputLabel>
            <Select label={label} value={flt[k] as string} onChange={(e) => setFlt({ ...flt, [k]: e.target.value })}>
              <MenuItem value="all">all</MenuItem>
              {values.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        ))}
        <FormControlLabel control={<Switch checked={flt.listedOnly} onChange={(e) => setFlt({ ...flt, listedOnly: e.target.checked })} />} label="Listed only" />
        <Typography variant="caption" color="text.secondary">{shown.length} of {rows.length} rows</Typography>
      </Box>
      <TableContainer component={Card} sx={{ bgcolor: '#2d2d2d', maxHeight: '70vh' }}>
        <Table size="small" stickyHeader sx={{ '& td, & th': { fontSize: 12, whiteSpace: 'nowrap', py: 0.5 } }}>
          <TableHead><TableRow>{cols.map((c) => <TableCell key={c} sx={{ bgcolor: '#333' }}>{c}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {shown.map((r, i) => {
              const ret = directionReturn(r.e);
              return (
                <TableRow key={i} hover>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.f.pdf_url ? <Link href={r.f.pdf_url} target="_blank" rel="noopener noreferrer">{r.f.document_number}</Link> : r.f.document_number}</TableCell>
                  <TableCell><Chip size="small" label={r.f.lane.replace('_', ' ')} color={LANE_COLOR[r.f.lane] || 'default'} sx={{ height: 20 }} /></TableCell>
                  <TableCell>{r.f.score}</TableCell>
                  <TableCell>{r.f.confidence ?? ''}</TableCell>
                  <TableCell>{r.f.material ? 'yes' : 'no'}</TableCell>
                  <TableCell>{r.f.event_stage}</TableCell>
                  <TableCell>{r.f.catalyst}</TableCell>
                  <TableCell>{r.f.agency}</TableCell>
                  <TableCell>{r.e.direction}</TableCell>
                  <TableCell>{r.e.company_name}</TableCell>
                  <TableCell>{r.e.model_ticker}</TableCell>
                  <TableCell>
                    {r.e.yahoo_symbol
                      ? <Link component="button" onClick={() => onSymbol(r.e.yahoo_symbol.toUpperCase())}>{r.e.yahoo_symbol}</Link>
                      : <Tooltip title={r.e.listing_note}><span style={{ color: '#999' }}>—</span></Tooltip>}
                  </TableCell>
                  <TableCell><Tooltip title={r.e.listing_note}><span>{r.e.listing}</span></Tooltip></TableCell>
                  <TableCell>{r.e.paper_line?.status}</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 220 }}>{(r.e.paper_line?.blockers || []).join(' · ')}</TableCell>
                  <TableCell>{cp(r, 'premarket_0846')}</TableCell>
                  <TableCell>{cp(r, 'open_0930')}</TableCell>
                  <TableCell>{cp(r, 'hour_1030')}</TableCell>
                  <TableCell>{cp(r, 'next_open_0930')}</TableCell>
                  <TableCell>{fmtPct(ret)}</TableCell>
                  <TableCell>{r.e.paper_line?.size ?? ''}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const addBusinessDay = (d: Date) => {
  const x = new Date(d.getTime() + 86400000);
  while (x.getUTCDay() === 0 || x.getUTCDay() === 6) x.setTime(x.getTime() + 86400000);
  return x;
};

// Same clock for stock and option: entry 9:35 ET when the signal printed before
// 9:35 (no fill 9:30-9:33), otherwise two minutes after the signal; exit 9:35 ET
// the next business day. Times only — fills come from the scanner.
const whatIfClock = (r: TapeRow): { entry: string; exit: string } => {
  if (!r.f.first_seen_at) return { entry: '9:35 ET', exit: 'next business day 9:35 ET' };
  const seen = new Date(r.f.first_seen_at);
  const hm = seen.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' });
  const entry = hm < '09:35' ? '9:35 ET' : etTime(new Date(seen.getTime() + 120000).toISOString());
  const exitDay = addBusinessDay(new Date(`${r.date}T12:00:00Z`)).toISOString().slice(0, 10);
  return { entry, exit: `${exitDay} 9:35 ET` };
};

const PaperBookView: React.FC<{ rows: TapeRow[]; history: History; counts: { cleared: number; near: number } }> = ({ rows, counts }) => {
  const book = rows.filter((r) => r.f.material && r.f.score >= MIN_SCORE && tradeable(r.e));
  if (!book.length) {
    return (
      <Card sx={{ bgcolor: '#2d2d2d' }}>
        <CardContent>
          <Typography variant="body1">
            Paper book is not open. No size has been set. This desk is a calibration sample: {counts.cleared} cleared, {counts.near} near misses.
            The next live signals are new document numbers after 8:44:45 AM ET on the next business day.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No recommendation has cleared 7 yet, so the what-if table is empty.
          </Typography>
        </CardContent>
      </Card>
    );
  }
  const money = (x: number | null | undefined) => (x == null ? '' : `${x >= 0 ? '+' : '−'}$${Math.abs(x).toFixed(2)}`);
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        What-if, not orders. One share and one contract, not multiplied by a size. Stock: LONG buys, SHORT sells short.
        Option: LONG one call, SHORT one put, 30–45 DTE, strike nearest the stock at entry, bid/ask mid.
        Fills and results come only from the scanner's what-if record; the 9:30 and next-open columns are the checkpoint path, not fills.
      </Typography>
      <TableContainer component={Card} sx={{ bgcolor: '#2d2d2d' }}>
        <Table size="small" sx={{ '& td, & th': { fontSize: 12, whiteSpace: 'nowrap' } }}>
          <TableHead><TableRow>
            {['Session', 'Document', 'Symbol', 'Dir', 'Score', 'Entry', 'Exit', 'Stock side', 'Stock entry', 'Stock exit', 'Stock / share',
              'Option (OCC)', 'Entry mid', 'Exit mid', 'Option / contract', '9:30 (path)', 'Next open (path)'].map((c) => <TableCell key={c}>{c}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {book.map((r, i) => {
              const clock = whatIfClock(r);
              const st = r.e.paper_line?.what_if?.stock;
              const op = r.e.paper_line?.what_if?.option;
              const short = r.e.direction === 'SHORT';
              const stStatus = st?.status || 'not_stored';
              const opStatus = op?.status || 'not_stored';
              // Prices and results only as the scanner wrote them; nothing computed here
              const stCell = (x?: number | null) => (x != null ? fmtPrice(x) : stStatus);
              const opCell = (mid?: number | null, bid?: number | null, ask?: number | null) => (mid != null ? (
                <Tooltip title={`bid ${fmtPrice(bid) || '—'} · ask ${fmtPrice(ask) || '—'}`}><span>{fmtPrice(mid)}</span></Tooltip>
              ) : opStatus);
              const path = (n: string) => { const x = checkpoint(r.e.paper_line, n); return x?.status === 'filled' ? fmtPrice(x.price) : ''; };
              const right = short ? 'put' : 'call';
              return (
                <TableRow key={i}>
                  <TableCell>{r.date}</TableCell><TableCell>{r.f.document_number}</TableCell>
                  <TableCell>{r.e.yahoo_symbol}</TableCell><TableCell>{r.e.direction}</TableCell><TableCell>{r.f.score}</TableCell>
                  <TableCell>{clock.entry}</TableCell><TableCell>{clock.exit}</TableCell>
                  <TableCell>{st?.side === 'short_sale' ? 'short sale' : st?.side || (short ? 'short sale' : 'buy')}</TableCell>
                  <TableCell>{stCell(st?.entry_price)}</TableCell>
                  <TableCell>{stCell(st?.exit_price)}</TableCell>
                  <TableCell>{money(st?.result_per_share)}</TableCell>
                  <TableCell>
                    {op?.contract
                      ? <Tooltip title={`${op.right || right} · strike ${op.strike ?? '—'} · exp ${op.expiration || '—'}`}><span>{op.contract}</span></Tooltip>
                      : `1 ${op?.right || right}`}
                  </TableCell>
                  <TableCell>{opCell(op?.entry_mid, op?.entry_bid, op?.entry_ask)}</TableCell>
                  <TableCell>{opCell(op?.exit_mid, op?.exit_bid, op?.exit_ask)}</TableCell>
                  <TableCell>{money(op?.result_per_contract)}</TableCell>
                  <TableCell>{path('open_0930')}</TableCell><TableCell>{path('next_open_0930')}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const CalibrationView: React.FC<{ ideas: { date: string; f: Filing }[]; rows: TapeRow[]; counts: { cleared: number; near: number; context: number }; required: number }> = ({ ideas, rows, counts, required }) => {
  const byScore = Array.from({ length: 10 }, (_, i) => ({ name: String(i + 1), n: ideas.filter(({ f }) => f.score === i + 1).length }));
  const filledDays = new Set(rows.filter((r) => {
    const c = checkpoint(r.e.paper_line, 'next_open_0930');
    return c?.status === 'filled' && c.price != null;
  }).map((r) => r.date)).size;
  const buckets = Array.from({ length: 10 }, (_, i) => {
    const xs = rows.filter((r) => r.f.score === i + 1).map((r) => directionReturn(r.e)).filter((x): x is number => x != null);
    return { score: i + 1, n: xs.length, med: xs.length ? median(xs) : null };
  });
  const anyReturns = buckets.some((b) => b.n > 0);
  return (
    <Box>
      {filledDays < required && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>Observation window. n={filledDays}. No edge claimed.</Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MiniBar title="Filings by score (all sessions; line = brief gate, not a measured edge)" data={byScore} refAt="7" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#2d2d2d', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Lanes (all sessions)</Typography>
              <Box sx={{ display: 'flex', gap: 3, my: 1 }}>
                {[['Cleared', counts.cleared], ['Near miss', counts.near], ['Context', counts.context]].map(([l, n]) => (
                  <Box key={l as string}><Typography variant="caption" color="text.secondary">{l}</Typography><Typography variant="h5">{n}</Typography></Box>
                ))}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">Trading days with a filled next-open checkpoint</Typography>
              <Typography variant="h5">{filledDays} / {required}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ bgcolor: '#2d2d2d' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Median direction return by score (9:30 → next 9:30, underlying)</Typography>
              {anyReturns ? (
                <Table size="small">
                  <TableHead><TableRow>{buckets.map((b) => <TableCell key={b.score} align="center">{b.score}</TableCell>)}</TableRow></TableHead>
                  <TableBody><TableRow>
                    {buckets.map((b) => (
                      <TableCell key={b.score} align="center">{b.med == null ? '—' : `${fmtPct(b.med)} (n=${b.n})`}</TableCell>
                    ))}
                  </TableRow></TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No direction returns yet — both checkpoint prices are needed.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FedTradeView;
